import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { BookOpen, Mail, Lock, User, Building2, FileText, Upload, Check, X, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    nom_entreprise: '',
    siret: ''
  });
  const [document, setDocument] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Validation du mot de passe
  const passwordValidation = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
    match: formData.password === formData.confirmPassword && formData.password.length > 0
  };

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non autorisé. Utilisez PDF, JPG ou PNG.');
      return;
    }

    if (file.size > maxSize) {
      toast.error('Fichier trop volumineux (max 10 MB)');
      return;
    }

    setDocument(file);
    toast.success(`Document sélectionné: ${file.name}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Veuillez corriger les erreurs dans le mot de passe');
      return;
    }

    if (!document) {
      toast.error('Veuillez ajouter un document justificatif (KBIS ou autre)');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('email', formData.email);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('nom_entreprise', formData.nom_entreprise);
      formDataToSend.append('siret', formData.siret || '');
      formDataToSend.append('document', document);

      await axios.post(`${API_URL}/api/auth/register`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      toast.success('Inscription enregistrée !');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'inscription';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const PasswordRule = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-xs ${valid ? 'text-emerald-600' : 'text-slate-400'}`}>
      {valid ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{text}</span>
    </div>
  );

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl text-emerald-600">Inscription enregistrée !</CardTitle>
            <CardDescription className="text-base mt-2">
              Votre demande est en cours de traitement
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Prochaines étapes :</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Un administrateur va vérifier vos documents</li>
                    <li>Une fois approuvé, vous recevrez un email</li>
                    <li>Cet email contiendra votre <strong>code de connexion</strong></li>
                    <li>Vous aurez <strong>1 mois d'essai gratuit</strong></li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                <strong>⏱️ Délai :</strong> La validation est généralement effectuée sous 24h ouvrées.
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Link to="/login" className="w-full">
              <Button variant="outline" className="w-full">
                Retour à la connexion
              </Button>
            </Link>
            <Link to="/" className="w-full">
              <Button variant="ghost" className="w-full text-slate-600">
                Retour à l'accueil
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="w-full max-w-lg">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-600 rounded-xl">
                <BookOpen className="h-8 w-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl">Créer un compte</CardTitle>
            <CardDescription>
              Inscrivez-vous pour accéder à 5minCompta
            </CardDescription>
          </CardHeader>
          
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {/* Informations personnelles */}
              <div className="space-y-2">
                <Label htmlFor="name">Nom complet *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="jean@exemple.fr"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Informations entreprise */}
              <div className="pt-2 border-t">
                <p className="text-sm font-medium text-slate-700 mb-3">Informations entreprise</p>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nom_entreprise">Nom de l'entreprise *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input
                        id="nom_entreprise"
                        type="text"
                        placeholder="Ma Micro-Entreprise"
                        value={formData.nom_entreprise}
                        onChange={(e) => setFormData({ ...formData, nom_entreprise: e.target.value })}
                        className="pl-10"
                        required
                        data-testid="entreprise-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="siret">SIRET (optionnel)</Label>
                    <Input
                      id="siret"
                      type="text"
                      placeholder="123 456 789 00012"
                      value={formData.siret}
                      onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                      data-testid="siret-input"
                    />
                  </div>
                </div>
              </div>

              {/* Upload document */}
              <div className="pt-2 border-t">
                <Label className="mb-2 block">Document justificatif *</Label>
                <p className="text-xs text-slate-500 mb-3">
                  KBIS, attestation INSEE, ou tout document prouvant la création de votre entreprise
                </p>
                
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragActive ? 'border-blue-500 bg-blue-50' : 
                    document ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-blue-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {document ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="h-8 w-8 text-emerald-600" />
                      <div className="text-left">
                        <p className="font-medium text-emerald-700">{document.name}</p>
                        <p className="text-xs text-emerald-600">
                          {(document.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDocument(null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 mb-1">
                        Glissez-déposez votre document ici
                      </p>
                      <p className="text-xs text-slate-400 mb-3">
                        PDF, JPG ou PNG (max 10 MB)
                      </p>
                      <label htmlFor="file-upload">
                        <Button type="button" variant="outline" size="sm" asChild>
                          <span>Parcourir</span>
                        </Button>
                      </label>
                      <input
                        id="file-upload"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                        data-testid="document-input"
                      />
                    </>
                  )}
                </div>
              </div>

              {/* Mot de passe */}
              <div className="pt-2 border-t space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="password-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <PasswordRule valid={passwordValidation.length} text="8 caractères minimum" />
                    <PasswordRule valid={passwordValidation.uppercase} text="Une majuscule" />
                    <PasswordRule valid={passwordValidation.lowercase} text="Une minuscule" />
                    <PasswordRule valid={passwordValidation.number} text="Un chiffre" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                  {formData.confirmPassword && (
                    <PasswordRule valid={passwordValidation.match} text="Les mots de passe correspondent" />
                  )}
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4">
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading || !isPasswordValid || !document}
                data-testid="register-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Inscription en cours...
                  </>
                ) : (
                  "S'inscrire"
                )}
              </Button>
              
              <p className="text-sm text-slate-500 text-center">
                Déjà inscrit ?{' '}
                <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                  Se connecter
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
